import React from "react";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";

import { DateOrTime } from "../../date-or-time";

import { Categories } from "./categories";
import { CTAIcon } from "./cta-icon";

import styles from "./styles.modules.scss";

type EntryType =
    | "imageNewsEntry"
    | "externalNewsEntry"
    | "newsEntry"
    | "esportNewsEntry"
    | "communityEntry"
    | "liveStreamCommunityEntry"
    | "videoNewsEntry";

type Icon = "external" | "video" | "internal" | "none";

type Type = "communityEntry" | "liveStreamCommunityEntry" | "videoNewsEntry";

const getIcon = (type?: EntryType): Icon => {
    if (type === "externalNewsEntry") {
        return "external";
    }

    if (
        type === "videoNewsEntry" ||
        type === "communityEntry" ||
        type === "liveStreamCommunityEntry"
    ) {
        return "video";
    }

    if (type === "imageNewsEntry") {
        return "none";
    }

    return "internal";
};

const getProvider = (type?: Type, provider?: string): string | undefined => {
    if (
        type === "videoNewsEntry" ||
        type === "liveStreamCommunityEntry" ||
        type === "communityEntry"
    ) {
        return provider;
    }

    return undefined;
};

export const ContentCardCaption = ({
    author,
    cardId,
    categories,
    creator,
    ctaHidden,
    external,
    icon,
    postDate,
    title,
    provider,
    type,
}: {
    author?: string;
    creator?: string;
    cardId: string | number;
    categories?: { title: string; color: string }[];
    ctaHidden?: boolean;
    external?: boolean;
    icon?: string;
    postDate?: number;
    title?: string;
    provider?: string;
    type?: "communityEntry" | "liveStreamCommunityEntry" | "videoNewsEntry";
}) => {
    const limit = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

    const footerIcon = getIcon(type);
    const footerProvider = getProvider(type, provider);

    return (
        <div
            className={classNames("caption", styles.caption, {
                [styles.columnLayout || "columnLayout"]: footerIcon === "video",
            })}
        >
            <div className={`details ${styles.details}`}>
                {categories && categories.length > 0 && (
                    <div className={`categories ${styles.categories}`}>
                        <Categories cardId={cardId} categories={categories} />
                    </div>
                )}

                {postDate && (
                    <div className={`post-date ${styles.postDate}`}>
                        <DateOrTime
                            limitInMilliseconds={limit}
                            timeStamp={postDate}
                        />
                    </div>
                )}

                {icon && <img className={`icon ${styles.icon}`} src={icon} />}

                {creator && (
                    <div className={`creator ${styles.creator}`}>
                        <div className={`prefix ${styles.prefix}`}>
                            <FormattedMessage id="globalText.langBy" />
                        </div>

                        <div className={`value ${styles.value}`}>{creator}</div>
                    </div>
                )}

                {title && (
                    <div className={`title ${styles.title}`}>{title}</div>
                )}

                {author && (
                    <div className={`author ${styles.author}`}>
                        <div className={`prefix ${styles.prefix}`}>
                            <FormattedMessage id="globalText.langBy" />
                        </div>

                        <div className={`value ${styles.value}`}>{author}</div>
                    </div>
                )}
            </div>

            {!ctaHidden && footerIcon !== "video" && (
                <div className={classNames("cta", styles.cta, provider)}>
                    <CTAIcon external={!!external} />
                </div>
            )}

            {footerIcon === "video" && (
                <div className={`footer`}>
                    <div className={`icon ${footerIcon}`} />
                    <div className={`provider ${footerProvider}`} />
                </div>
            )}
        </div>
    );
};
